
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';

import { ConferenceHotel, ConferenceHotelResponse } from '../../types/conferenceHotel';
import { APIHOST } from '../../common';
import { OrderFormProps } from '../../lib/order';
import { useApp } from '../../components/AppContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Order } from '../../types/order';
import { fetchOrder } from '../../lib/order';


export default function HotelSelector({
  orderId,
  back,
  onSave,
  type
}: OrderFormProps) {
  const [hotels, setHotels] = useState<ConferenceHotel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedAmenities, setExpandedAmenities] = useState<Record<string, boolean>>({});
  const [selectedConferenceHotelId, setSelectedConferenceHotelId] = useState<string | null>(null);

  const { setSnackbarInput } = useApp();

  useEffect(() => {
    
      fetchHotels();
      fetchOrder(orderId)        
        .then(order => {
          if (!order) {
            return;
          }
          setSelectedConferenceHotelId(order.conference_hotel?.documentId || null);
        })
        .catch(err => {
          console.error('Error loading order:', err);
          setSnackbarInput({ message: 'Error loading order details', severity: 'error' });
        });
    
  }, []);


  const fetchHotels = async () => {
    
    try {
      setLoading(true);
      setError('');
      
      
      const response = await fetch(
        `${APIHOST}/api/conference-hotels/available?orderId=${orderId}`,
        { 
          method: 'GET', 
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
          } }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch hotels');
      }
      
      const data: ConferenceHotelResponse = await response.json();
      console.log('Fetched hotels:', data);
   
      setHotels(data.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading hotels');
      console.error('Error fetching hotels:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleHotelSelect = async (hotel: ConferenceHotel) => {
    console.log('handleHotelSelect:', hotel);
    // Proceed with hotel selection
    await confirmHotelSelection(hotel);
  };

  const confirmHotelSelection = async (conference_hotel: ConferenceHotel) => {


    // Save hotel selection to API
    try {
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        const response = await fetch(`${APIHOST}/api/orders/${orderId}/setHotel`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            data: {
              conferenceHotelId: conference_hotel.documentId
            }
          }),
        });

        if (!response.ok) {
          console.error('Failed to save hotel selection to API');
          setSnackbarInput({ message: 'Failed to save hotel selection', severity: 'error' });
        } else {
          setSelectedConferenceHotelId(conference_hotel.documentId);
          setSnackbarInput({ message: 'Hotel selection saved successfully', severity: 'success' });
        }
      }
    } catch (error) {
      console.error('Error saving hotel selection:', error);
      setSnackbarInput({ message: 'Error saving hotel selection', severity: 'error' });
    }
  }

  const formatPrice = (price: number | null | undefined) => {
    if (!price) return 'Contact for pricing';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const truncateAmenities = (amenities: string, maxLength: number = 60) => {
    if (amenities.length <= maxLength) return amenities;
    return amenities.substring(0, maxLength) + '...';
  };

  const toggleAmenitiesExpansion = (hotelId: string) => {
    setExpandedAmenities(prev => ({
      ...prev,
      [hotelId]: !prev[hotelId]
    }));
  };



  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading available hotels...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (hotels.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h6" gutterBottom>
          No Hotels Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are no hotels currently available for this conference.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight="600" sx={{ mb: 3 }}>
        Select Your Hotel
      </Typography>

      {/* Hotel Cards Grid */}
      <Grid container spacing={3}>
        {hotels.map((conferenceHotel) => (
          <Grid size={{ xs: 12, md: 6 }} key={conferenceHotel.hotel.documentId}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                border: selectedConferenceHotelId === conferenceHotel.documentId ? 2 : 1,
                borderColor: selectedConferenceHotelId === conferenceHotel.documentId ? 'primary.main' : 'grey.800',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="h4" gutterBottom>
                  {conferenceHotel.hotel.longName}
                </Typography>

                {/* {conferenceHotel.hotel.addressCity && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {conferenceHotel.hotel.addressCity && `${conferenceHotel.hotel.addressCity}`}
                    {conferenceHotel.hotel.addressState && `, ${conferenceHotel.hotel.addressState}`}
                    {conferenceHotel.hotel.addressZip && ` ${conferenceHotel.hotel.addressZip}`}
                  </Typography>
                )} */}

                {conferenceHotel.hotel.website && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    <Link href={conferenceHotel.hotel.website} target="_blank" rel="noopener noreferrer">
                      Website
                    </Link>
                  </Typography>
                )}

                {/* Available Rooms Section */}
                
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="600" gutterBottom>
                      Available Rooms:
                    </Typography>
                    {conferenceHotel.conference_hotel_rooms.map((room) => (
                      <Box key={room.documentId} sx={{ 
                        mb: 1, 
                        p: 1.5, 
                        border: 1, 
                        borderColor: 'grey.900', 
                        borderRadius: 1,
                      
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="600">
                            {room.name}
                          </Typography>
                          <Typography variant="body2" fontWeight="600" color="primary.main">
                            {formatPrice(room.dailyRate)}/night
                          </Typography>
                        </Box>
                        <Box >
                          <Typography variant="caption" color="text.secondary">
                            {room.bedCount} bed{room.bedCount > 1 && 's'}
                          </Typography>
                          {room.maxOccupants && (
                            <Typography variant="caption" color="text.secondary">
                              <br />
                              Max {room.maxOccupants} guests
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                

                {conferenceHotel.hotel.amenities && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="600" gutterBottom>
                      Amenities:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {expandedAmenities[conferenceHotel.hotel.documentId] 
                        ? conferenceHotel.hotel.amenities 
                        : truncateAmenities(conferenceHotel.hotel.amenities)
                      }
                      {conferenceHotel.hotel.amenities.length > 60 && (
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAmenitiesExpansion(conferenceHotel.hotel.documentId);
                          }}
                          sx={{ 
                            ml: 1, 
                            minWidth: 'auto', 
                            p: 0,
                            textTransform: 'none',
                            fontSize: '0.75rem'
                          }}
                        >
                          {expandedAmenities[conferenceHotel.hotel.documentId] ? 'Show less' : 'Show more'}
                        </Button>
                      )}
                    </Typography>
                  </Box>
                )}
              </CardContent>
              
              <CardActions>
                <Button
                  variant={selectedConferenceHotelId === conferenceHotel.documentId ? "contained" : "outlined"}
                  color="primary"
                  fullWidth
                  onClick={() => handleHotelSelect(conferenceHotel)}
                >
                  {selectedConferenceHotelId === conferenceHotel.documentId ? "Selected" : "Select Hotel"}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box>
        {back && <Button
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={back} 
          disabled={!back} >
          Back
        </Button>}
        <Button
          color="primary"
          size="small"
          variant="contained"
          startIcon={<ArrowForwardIcon fontSize="small" />}
          sx={{px: 4, py: 0.5, mt: 2}}
          onClick={onSave}
          disabled={!selectedConferenceHotelId}
        >
          {type === 'step' ? 'Next' : 'Save'}
        </Button>
      </Box>

    </Box>
  );
}